import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Alert, AlertTitle, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, FileText, ExternalLink, CheckCircle2, Info, ScanLine, Layers } from 'lucide-react';

const NORM_GROUPS = (lang) => [
  {
    title: lang === 'fr' ? 'Marges (m)' : 'Setbacks (m)',
    fields: [
      ['marge_avant_min_m', lang === 'fr' ? 'Avant minimale' : 'Front min'],
      ['marge_avant_max_m', lang === 'fr' ? 'Avant maximale' : 'Front max'],
      ['marge_avant_secondaire_min_m', lang === 'fr' ? 'Avant secondaire min.' : 'Secondary front min'],
      ['marge_laterale_min_m', lang === 'fr' ? 'Latérale minimale' : 'Side min'],
      ['marges_laterales_totales_min_m', lang === 'fr' ? 'Latérales totales min.' : 'Total sides min'],
      ['marge_arriere_min_m', lang === 'fr' ? 'Arrière minimale' : 'Rear min'],
    ],
  },
  {
    title: lang === 'fr' ? 'Bâtiment principal' : 'Main building',
    fields: [
      ['etages_min', lang === 'fr' ? 'Étages min.' : 'Storeys min'],
      ['etages_max', lang === 'fr' ? 'Étages max.' : 'Storeys max'],
      ['hauteur_min_m', lang === 'fr' ? 'Hauteur min. (m)' : 'Height min (m)'],
      ['hauteur_max_m', lang === 'fr' ? 'Hauteur max. (m)' : 'Height max (m)'],
      ['largeur_min_m', lang === 'fr' ? 'Largeur min. (m)' : 'Width min (m)'],
      ['profondeur_min_m', lang === 'fr' ? 'Profondeur min. (m)' : 'Depth min (m)'],
      ['sup_implantation_sol_min_m2', lang === 'fr' ? 'Sup. implantation au sol min. (m²)' : 'Footprint min (m²)'],
      ['sup_implantation_sol_max_m2', lang === 'fr' ? 'Sup. implantation au sol max. (m²)' : 'Footprint max (m²)'],
      ['sup_plancher_min_m2', lang === 'fr' ? 'Sup. de plancher min. (m²)' : 'Floor area min (m²)'],
      ['sup_plancher_max_m2', lang === 'fr' ? 'Sup. de plancher max. (m²)' : 'Floor area max (m²)'],
    ],
  },
  {
    title: lang === 'fr' ? 'Rapports' : 'Ratios',
    fields: [
      ['rapport_bati_terrain_min', lang === 'fr' ? 'Bâti / terrain min.' : 'Building/lot min'],
      ['rapport_bati_terrain_max', lang === 'fr' ? 'Bâti / terrain max.' : 'Building/lot max'],
      ['rapport_plancher_terrain_min', lang === 'fr' ? 'Plancher / terrain min.' : 'Floor/lot min'],
      ['rapport_plancher_terrain_max', lang === 'fr' ? 'Plancher / terrain max.' : 'Floor/lot max'],
    ],
  },
  {
    title: lang === 'fr' ? 'Lotissement' : 'Subdivision',
    fields: [
      ['lot_largeur_interieur_min_m', lang === 'fr' ? 'Largeur min. lot intérieur (m)' : 'Interior lot width min (m)'],
      ['lot_largeur_angle_min_m', lang === 'fr' ? 'Largeur min. lot d\u2019angle (m)' : 'Corner lot width min (m)'],
      ['lot_profondeur_min_m', lang === 'fr' ? 'Profondeur min. (m)' : 'Depth min (m)'],
      ['lot_superficie_min_m2', lang === 'fr' ? 'Superficie min. (m²)' : 'Area min (m²)'],
    ],
  },
];

const MODE_LABELS = { isolee: 'Isolée', jumelee: 'Jumelée', contigue: 'Contiguë' };

const NormValue = ({ field, lang }) => {
  if (!field || field.value === null || field.value === undefined) {
    return <span className="text-muted-foreground italic text-sm">{lang === 'fr' ? 'Non prescrit' : 'Not prescribed'}</span>;
  }
  return <span className="font-mono font-semibold">{typeof field.value === 'number' ? field.value.toLocaleString('fr-CA') : String(field.value)}</span>;
};

const NormSetTable = ({ normSet, lang }) => (
  <div className="space-y-4" data-testid={`st-norm-table-${normSet.set_id}`}>
    <div className="flex flex-wrap gap-2 items-center">
      <span className="text-xs uppercase text-muted-foreground">{lang === 'fr' ? 'Implantation' : 'Structure type'} :</span>
      {Object.entries(normSet.implantation_modes || {}).filter(([, v]) => v).map(([k]) => (
        <Badge key={k} className="bg-accent/15 text-primary border border-accent/40" data-testid={`st-mode-badge-${k}`}>{MODE_LABELS[k]}</Badge>
      ))}
      {!Object.values(normSet.implantation_modes || {}).some(Boolean) && (
        <span className="text-muted-foreground italic text-sm">{lang === 'fr' ? 'Non prescrit' : 'Not prescribed'}</span>
      )}
    </div>
    {Object.keys(normSet.nb_logements || {}).length > 0 && (
      <div className="flex flex-wrap gap-4 text-sm">
        {Object.entries(normSet.nb_logements).map(([k, v]) => (
          <span key={k}>
            <span className="text-muted-foreground">{v.libelle_grille || k} :</span>{' '}
            <NormValue field={v} lang={lang} />
          </span>
        ))}
      </div>
    )}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {NORM_GROUPS(lang).map((group) => (
        <div key={group.title} className="border rounded-lg p-3">
          <p className="text-xs uppercase font-bold text-muted-foreground mb-2">{group.title}</p>
          <div className="space-y-1">
            {group.fields.map(([f, label]) => (
              <div key={f} className="flex justify-between gap-2 text-sm border-b border-dashed border-border/50 last:border-0 py-0.5">
                <span>{label}</span>
                <NormValue field={normSet.normes?.[f]} lang={lang} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const UsagesBlock = ({ normSet, lang }) => (
  <div className="space-y-2 text-sm" data-testid={`st-usages-${normSet.set_id}`}>
    {normSet.usages_autorises?.length > 0 && (
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-xs uppercase text-muted-foreground">{lang === 'fr' ? 'Classes autorisées' : 'Permitted classes'} :</span>
        {normSet.usages_autorises.map((u) => (
          <Badge key={u.code_classe} variant="outline" className="font-mono">
            {u.code_classe} — {u.label}{u.renvoi_disposition ? ` (disp. ${u.renvoi_disposition})` : ''}
          </Badge>
        ))}
      </div>
    )}
    {normSet.usages_specifiquement_permis?.length > 0 && (
      <p>
        <span className="text-xs uppercase text-muted-foreground">{lang === 'fr' ? 'Spécifiquement permis' : 'Specifically permitted'} :</span>{' '}
        <span className="font-mono text-xs">{normSet.usages_specifiquement_permis.join(', ')}</span>
      </p>
    )}
    {normSet.usages_specifiquement_exclus?.length > 0 && (
      <p>
        <span className="text-xs uppercase text-muted-foreground">{lang === 'fr' ? 'Spécifiquement exclus' : 'Specifically excluded'} :</span>{' '}
        <span className="font-mono text-xs text-destructive">{normSet.usages_specifiquement_exclus.join(', ')}</span>
      </p>
    )}
  </div>
);

export const SteThereseZoneSheet = ({ zoneCode, language }) => {
  const lang = language || 'fr';
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usageFilter, setUsageFilter] = useState('all');
  const [modeFilter, setModeFilter] = useState('all');

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/regulations/ste-therese/${encodeURIComponent(zoneCode)}`)
      .then((res) => { if (active) setDoc(res.data); })
      .catch((e) => { if (active) setError(e.response?.data?.detail || 'Erreur de chargement de la zone'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [zoneCode]);

  const usageOptions = useMemo(() => {
    if (!doc?.norm_sets) return [];
    const map = new Map();
    doc.norm_sets.forEach((ns) => (ns.usages_autorises || []).forEach((u) => map.set(u.code_classe, u.label)));
    return [...map.entries()].sort();
  }, [doc]);

  const matched = useMemo(() => {
    if (!doc?.norm_sets) return [];
    return doc.norm_sets.filter((ns) => {
      const modeOk = modeFilter === 'all' || ns.implantation_modes?.[modeFilter];
      const usageOk = usageFilter === 'all' || (ns.usages_autorises || []).some((u) => u.code_classe === usageFilter);
      return modeOk && usageOk;
    });
  }, [doc, usageFilter, modeFilter]);

  // Jeux non filtrés par classe mais portant uniquement des codes fins : présentés plutôt que filtrés silencieusement
  const specOnlyPresented = useMemo(() => {
    if (!doc?.norm_sets || usageFilter === 'all') return [];
    return doc.norm_sets.filter((ns) =>
      !matched.includes(ns)
      && (ns.usages_autorises || []).length === 0
      && (ns.usages_specifiquement_permis || []).length > 0
      && (modeFilter === 'all' || ns.implantation_modes?.[modeFilter])
    );
  }, [doc, matched, usageFilter, modeFilter]);

  if (loading) {
    return <div className="flex items-center gap-2 text-muted-foreground p-8" data-testid="st-zone-loading"><Loader2 className="w-4 h-4 animate-spin" /> {lang === 'fr' ? 'Chargement de la grille…' : 'Loading grid…'}</div>;
  }
  if (error) {
    return (
      <Alert variant="destructive" data-testid="st-zone-error">
        <AlertTitle>{lang === 'fr' ? 'Zone introuvable' : 'Zone not found'}</AlertTitle>
        <AlertDescription>{String(error)}</AlertDescription>
      </Alert>
    );
  }

  const filtersActive = usageFilter !== 'all' || modeFilter !== 'all';
  const visibleSets = matched.concat(specOnlyPresented);

  return (
    <div className="space-y-6" data-testid="st-zone-sheet">
      {/* En-tête */}
      <div className="flex flex-wrap items-start justify-between gap-3 border-b pb-4">
        <div>
          <h2 className="font-heading text-lg font-bold uppercase">
            {lang === 'fr' ? 'Grille des usages et normes' : 'Uses and standards grid'} — <span className="font-mono">{doc.zone_code}</span>
          </h2>
          <p className="text-sm text-muted-foreground">
            {doc.jurisdiction_name} • {lang === 'fr' ? 'Règlement de zonage' : 'Zoning by-law'} {doc.reglement_code} • {doc.zone_category}
          </p>
        </div>
        <a href={doc.source_url} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-accent underline" data-testid="st-source-link">
          <FileText className="w-4 h-4" /> {lang === 'fr' ? 'Grille officielle (PDF)' : 'Official grid (PDF)'} <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* Grille scannée : saisie manuelle requise */}
      {doc.extraction_status === 'scanned_manual_required' ? (
        <Alert className="border-amber-300 bg-amber-50" data-testid="st-scanned-alert">
          <ScanLine className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-900">{lang === 'fr' ? 'Grille numérisée — saisie manuelle requise' : 'Scanned grid — manual entry required'}</AlertTitle>
          <AlertDescription className="text-amber-800">
            {doc.manual_entry_note}
            <br />
            <a href={doc.source_url} target="_blank" rel="noopener noreferrer" className="underline font-medium">
              {lang === 'fr' ? 'Consulter la grille scannée sur le site de la Ville' : 'View the scanned grid on the City website'}
            </a>
          </AlertDescription>
        </Alert>
      ) : (
        <>
          {/* Sélecteur usage / mode d'implantation */}
          <Card className="border-l-4 border-l-accent" data-testid="st-selector-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <Layers className="w-4 h-4 text-accent" />
                {lang === 'fr' ? 'Que voulez-vous construire ?' : 'What do you want to build?'}
              </CardTitle>
              <CardDescription>
                {doc.multiple_norm_sets
                  ? (lang === 'fr'
                    ? `Cette zone contient ${doc.norm_sets.length} jeux de normes distincts. Les normes applicables dépendent de l'usage et du mode d'implantation de votre projet.`
                    : `This zone has ${doc.norm_sets.length} distinct standard sets. Applicable standards depend on your project's use and structure type.`)
                  : (lang === 'fr' ? 'Cette zone contient un seul jeu de normes.' : 'This zone has a single standard set.')}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-4">
              <div className="min-w-[220px]">
                <p className="text-xs uppercase text-muted-foreground mb-1">{lang === 'fr' ? 'Usage visé' : 'Intended use'}</p>
                <Select value={usageFilter} onValueChange={setUsageFilter}>
                  <SelectTrigger data-testid="st-usage-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{lang === 'fr' ? '— Non précisé —' : '— Not specified —'}</SelectItem>
                    {usageOptions.map(([code, label]) => (
                      <SelectItem key={code} value={code}>{code} — {label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[220px]">
                <p className="text-xs uppercase text-muted-foreground mb-1">{lang === 'fr' ? "Mode d'implantation" : 'Structure type'}</p>
                <Select value={modeFilter} onValueChange={setModeFilter}>
                  <SelectTrigger data-testid="st-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{lang === 'fr' ? '— Non précisé —' : '— Not specified —'}</SelectItem>
                    <SelectItem value="isolee">Isolée</SelectItem>
                    <SelectItem value="jumelee">Jumelée</SelectItem>
                    <SelectItem value="contigue">Contiguë</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Issue 1 : un seul jeu applicable */}
          {filtersActive && visibleSets.length === 1 && (
            <Alert className="border-green-300 bg-green-50" data-testid="st-single-match-banner">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertTitle className="text-green-900">
                {lang === 'fr'
                  ? `Un seul jeu de normes s'applique : colonne ${visibleSets[0].set_id}`
                  : `A single standard set applies: column ${visibleSets[0].set_id}`}
              </AlertTitle>
              <AlertDescription className="text-green-800">{visibleSets[0].usage_summary}</AlertDescription>
            </Alert>
          )}

          {/* Issue 3 : aucun jeu — réponse réglementaire utile, pas une erreur */}
          {filtersActive && visibleSets.length === 0 && (
            <Alert className="border-blue-300 bg-blue-50" data-testid="st-no-match-card">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertTitle className="text-blue-900">
                {lang === 'fr' ? 'Réponse réglementaire : combinaison non autorisée' : 'Regulatory answer: combination not permitted'}
              </AlertTitle>
              <AlertDescription className="text-blue-800 space-y-2">
                <p>
                  {lang === 'fr'
                    ? `Aucun jeu de normes de la zone ${doc.zone_code} n'autorise ${usageFilter !== 'all' ? `l'usage ${usageFilter}` : 'cet usage'}${modeFilter !== 'all' ? ` en implantation ${MODE_LABELS[modeFilter].toLowerCase()}` : ''}. Ce n'est pas une erreur : la grille officielle ne permet pas cette combinaison.`
                    : `No standard set in zone ${doc.zone_code} permits ${usageFilter !== 'all' ? `use ${usageFilter}` : 'this use'}${modeFilter !== 'all' ? ` as ${MODE_LABELS[modeFilter].toLowerCase()}` : ''}. This is not an error: the official grid does not allow this combination.`}
                </p>
                <p className="text-sm">
                  {lang === 'fr' ? 'Combinaisons autorisées dans cette zone :' : 'Permitted combinations in this zone:'}
                </p>
                <ul className="list-disc pl-5 text-sm">
                  {doc.norm_sets.map((ns) => (
                    <li key={ns.set_id}><span className="font-mono font-bold">{ns.set_id}</span> : {ns.usage_summary}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Issue 2 : plusieurs jeux (ou aucun filtre) — chaque norme rattachée à sa colonne */}
          {visibleSets.map((ns) => (
            <Card key={ns.set_id} data-testid={`st-set-card-${ns.set_id}`}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-heading flex flex-wrap items-center gap-2">
                  <Badge className="bg-primary text-primary-foreground font-mono">{lang === 'fr' ? 'Jeu' : 'Set'} {ns.set_id}</Badge>
                  <span>{ns.usage_summary}</span>
                  {specOnlyPresented.includes(ns) && (
                    <Badge variant="outline" className="text-amber-700 border-amber-400" data-testid={`st-spec-only-badge-${ns.set_id}`}>
                      {lang === 'fr' ? 'Usages spécifiques uniquement — vérifier les codes' : 'Specific uses only — check codes'}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <UsagesBlock normSet={ns} lang={lang} />
                <NormSetTable normSet={ns} lang={lang} />
              </CardContent>
            </Card>
          ))}

          {/* Dispositions de zone (communes à tous les jeux) */}
          <Card data-testid="st-zone-provisions">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading">{lang === 'fr' ? 'Dispositions de la zone' : 'Zone provisions'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div>
                <p className="text-xs uppercase text-muted-foreground mb-1">{lang === 'fr' ? 'Rappels (PIIA, PPU, PAE…)' : 'Reminders (SPAIP, PPU, PAE…)'}</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(doc.rappels || {}).filter(([, v]) => v).map(([k]) => (
                    <Badge key={k} className="bg-accent/15 text-primary border border-accent/40">{k}</Badge>
                  ))}
                  {!Object.values(doc.rappels || {}).some(Boolean) && (
                    <span className="text-muted-foreground italic">{lang === 'fr' ? 'Aucun indicateur coché' : 'No indicator checked'}</span>
                  )}
                </div>
                {(doc.rappels_notes || []).length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">{doc.rappels_notes.join(' • ')}</p>
                )}
              </div>
              {(doc.dispositions_speciales || []).length > 0 && (
                <div>
                  <p className="text-xs uppercase text-muted-foreground mb-1">{lang === 'fr' ? 'Dispositions spéciales' : 'Special provisions'}</p>
                  <ul className="list-disc pl-5 space-y-1">
                    {doc.dispositions_speciales.map((d, i) => <li key={i}>{d}</li>)}
                  </ul>
                </div>
              )}
              {(doc.articles_exclus || []).length > 0 && (
                <p>
                  <span className="text-xs uppercase text-muted-foreground">{lang === 'fr' ? 'Articles exclus' : 'Excluded articles'} :</span>{' '}
                  {doc.articles_exclus.join(' ; ')}
                </p>
              )}
              {(doc.amendements || []).length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {lang === 'fr' ? 'Amendements' : 'Amendments'} : {doc.amendements.join(' • ')}
                </p>
              )}
              {(doc.extraction_warnings || []).length > 0 && (
                <Alert className="border-amber-200 bg-amber-50/50" data-testid="st-extraction-warnings">
                  <Info className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800 text-xs">
                    {lang === 'fr'
                      ? `Particularités d'extraction consignées (${doc.extraction_warnings.length}) : `
                      : `Extraction notes (${doc.extraction_warnings.length}): `}
                    {doc.extraction_warnings.join(' • ')}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default SteThereseZoneSheet;
